/**
 * Event Emitter Utility
 *
 * Simple event emitter for handling application events
 */

import type { EventCallback, Subscription } from '@/types';

export class EventEmitter {
  private events: Map<string, EventCallback<unknown>[]> = new Map();

  /**
   * Subscribe to an event
   *
   * @param event Event name
   * @param callback Event callback
   * @returns Subscription object with unsubscribe method
   */
  public on<T = unknown>(event: string, callback: EventCallback<T>): Subscription {
    if (!this.events.has(event)) {
      this.events.set(event, []);
    }

    const callbacks = this.events.get(event);
    if (!callbacks) {
      // This shouldn't happen since we just set it above, but for safety
      this.events.set(event, [callback as EventCallback<unknown>]);
      return {
        unsubscribe: () => {
          const eventCallbacks = this.events.get(event);
          if (eventCallbacks) {
            const index = eventCallbacks.indexOf(callback as EventCallback<unknown>);
            if (index > -1) {
              eventCallbacks.splice(index, 1);
            }
          }
        },
      };
    }

    callbacks.push(callback as EventCallback<unknown>);

    // Return subscription object
    return {
      unsubscribe: () => {
        const index = callbacks.indexOf(callback as EventCallback<unknown>);
        if (index > -1) {
          callbacks.splice(index, 1);
        }
      },
    };
  }

  /**
   * Subscribe to an event once (auto-unsubscribe after first emission)
   *
   * @param event Event name
   * @param callback Event callback
   * @returns Subscription object
   */
  public once<T = unknown>(event: string, callback: EventCallback<T>): Subscription {
    const subscription = this.on(event, (data?: T) => {
      subscription.unsubscribe();
      callback(data);
    });

    return subscription;
  }

  /**
   * Emit an event
   *
   * @param event Event name
   * @param data Event data
   */
  public emit<T = unknown>(event: string, data?: T): void {
    const callbacks = this.events.get(event);
    if (!callbacks) {
      return;
    }

    // Call all callbacks with the data
    callbacks.forEach(callback => {
      try {
        callback(data);
      } catch (error) {
        console.error(`[EventEmitter] Error in event callback for '${event}':`, error);
      }
    });
  }

  /**
   * Remove all listeners for an event
   *
   * @param event Event name
   */
  public off(event: string): void {
    this.events.delete(event);
  }

  /**
   * Remove all event listeners
   */
  public clear(): void {
    this.events.clear();
  }

  /**
   * Get list of events with listeners
   *
   * @returns Array of event names
   */
  public getEvents(): string[] {
    return Array.from(this.events.keys());
  }

  /**
   * Get number of listeners for an event
   *
   * @param event Event name
   * @returns Number of listeners
   */
  public listenerCount(event: string): number {
    const callbacks = this.events.get(event);
    return callbacks ? callbacks.length : 0;
  }

  /**
   * Check if event has listeners
   *
   * @param event Event name
   * @returns Whether event has listeners
   */
  public hasListeners(event: string): boolean {
    return this.listenerCount(event) > 0;
  }
}
